import { AppConfig } from '@core/domain/AppConfig.ts';
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type { AppContainer } from './AppContainer.ts';
import type { AuthApplicationPort } from '@src/modules/auth/domain/ports/AuthApplicationPort.ts';
import type { ShellLayoutPort } from '@src/modules/shell-routing/infrastructure/driven/layout/DomShellLayoutAdapter.ts';
import type { ShellNavigationPort } from '@src/modules/shell-routing/domain/ports/ShellNavigationPort.ts';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';
import type { SystemApplicationService } from '@src/modules/system/application/SystemApplicationService.ts';
import type { EntregaApplicationService } from '@src/modules/entrega-paquetes/application/EntregaApplicationService.ts';
import type { EntregaCatalogRepositoryPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaPorts.ts';
import type { ReposicionApplicationService } from '@src/modules/reposicion-productos/application/ReposicionApplicationService.ts';
import type { GeolocationPort } from '@src/modules/entrega-paquetes/domain/ports/GeolocationPort.ts';
import type { EntregaMapPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaMapPort.ts';
import type { EntregaPdfPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaPdfPort.ts';
import type { ReposicionPdfPort } from '@src/modules/reposicion-productos/domain/ports/ReposicionPdfPort.ts';
import type { SignatureCapturePort } from '@src/modules/platform/signature/domain/ports/SignatureCapturePort.ts';
import { onLocalSqlitePeerUpdate } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePeerSync.ts';

/** Shared shell surface: layout, auth, system, notifications. */
export type ShellCorePorts = {
  notifications: NotificationPort;
  telemetry: TelemetryPort;
  navigation: ShellNavigationPort;
  layout: ShellLayoutPort;
  auth: AuthApplicationPort;
  appDisplayTitle: string;
  system: SystemApplicationService;
  /** Resume authenticated shell bootstrap after in-page login (catalog sync runs in orchestrator). */
  notifyLoginSuccess(): Promise<void>;
  /** Reload list/forms when another tab persists local SQLite. */
  onLocalSqlitePeerUpdate(handler: () => void): () => void;
};

export type EntregaRoutePorts = {
  entrega: EntregaApplicationService;
  geolocation: GeolocationPort;
  entregaMap: EntregaMapPort;
  entregaPdf: EntregaPdfPort;
};

export type ReposicionRoutePorts = {
  reposicion: ReposicionApplicationService;
  reposicionPdf: ReposicionPdfPort;
};

export type SignatureCaptureShellPort = {
  signatureCapture: SignatureCapturePort;
};

/** Entrega page drivers — no reposición surface. */
export type EntregaShellPorts = ShellCorePorts & EntregaRoutePorts & SignatureCaptureShellPort;

/** Reposición page drivers — no entrega/GPS surface. */
export type ReposicionShellPorts = ShellCorePorts & ReposicionRoutePorts & SignatureCaptureShellPort;

/** System/admin pages (sync, observability, update). */
export type SystemShellPorts = ShellCorePorts;

/** Sync page needs entrega catalog for offline map warmup after manual pull. */
export type SyncShellPorts = ShellCorePorts & {
  entregaCatalog: EntregaCatalogRepositoryPort;
};

/** Observability needs entrega/reposición read + export, not GPS/PDF. */
export type ObservabilityShellPorts = ShellCorePorts & {
  entrega: EntregaApplicationService;
  reposicion: ReposicionApplicationService;
};

export type LoginShellPorts = Pick<
  ShellCorePorts,
  'appDisplayTitle' | 'auth' | 'telemetry' | 'notifyLoginSuccess'
>;

/** Narrow ports injected into a mounted page element (runtime subset of bootstrap bundle). */
export type MainRouteShellPorts = ShellCorePorts | EntregaShellPorts | ReposicionShellPorts | ObservabilityShellPorts | SyncShellPorts;

export type RouteShellPorts = MainRouteShellPorts | LoginShellPorts;

/** Composition root bundle wired once in bootstrap; routes receive `RouteShellPorts` at mount. */
export type ShellPorts = ShellCorePorts & EntregaRoutePorts & ReposicionRoutePorts & SignatureCaptureShellPort;

export type ShellPageElement = HTMLElement & { shellPorts: RouteShellPorts };

export function pickShellCorePorts(ports: ShellPorts): ShellCorePorts {
  return {
    notifications: ports.notifications,
    telemetry: ports.telemetry,
    navigation: ports.navigation,
    layout: ports.layout,
    auth: ports.auth,
    appDisplayTitle: ports.appDisplayTitle,
    system: ports.system,
    notifyLoginSuccess: ports.notifyLoginSuccess,
    onLocalSqlitePeerUpdate: ports.onLocalSqlitePeerUpdate,
  };
}

export function pickEntregaShellPorts(ports: ShellPorts): EntregaShellPorts {
  return {
    ...pickShellCorePorts(ports),
    entrega: ports.entrega,
    geolocation: ports.geolocation,
    entregaMap: ports.entregaMap,
    entregaPdf: ports.entregaPdf,
    signatureCapture: ports.signatureCapture,
  };
}

export function pickReposicionShellPorts(ports: ShellPorts): ReposicionShellPorts {
  return {
    ...pickShellCorePorts(ports),
    reposicion: ports.reposicion,
    reposicionPdf: ports.reposicionPdf,
    signatureCapture: ports.signatureCapture,
  };
}

export function pickObservabilityShellPorts(ports: ShellPorts): ObservabilityShellPorts {
  return {
    ...pickShellCorePorts(ports),
    entrega: ports.entrega,
    reposicion: ports.reposicion,
  };
}

export function pickSyncShellPorts(ports: ShellPorts): SyncShellPorts {
  return {
    ...pickShellCorePorts(ports),
    entregaCatalog: ports.entrega.catalog,
  };
}

export function pickLoginShellPorts(ports: ShellPorts): LoginShellPorts {
  return {
    appDisplayTitle: ports.appDisplayTitle,
    auth: ports.auth,
    telemetry: ports.telemetry,
    notifyLoginSuccess: ports.notifyLoginSuccess,
  };
}

const PAGE_SHELL_PORT_PICKERS: Record<string, (ports: ShellPorts) => MainRouteShellPorts> = {
  'entrega-paquetes': pickEntregaShellPorts,
  'reposicion-productos': pickReposicionShellPorts,
  observability: pickObservabilityShellPorts,
  sync: pickSyncShellPorts,
};

export function shellPortsForPage(pageId: string, ports: ShellPorts): MainRouteShellPorts {
  return (PAGE_SHELL_PORT_PICKERS[pageId] ?? pickShellCorePorts)(ports);
}

export function createShellPorts(opts: {
  container: AppContainer;
  auth: AuthApplicationPort;
  navigation: ShellNavigationPort;
  layout: ShellLayoutPort;
  appDisplayTitle?: string;
  notifyLoginSuccess: () => Promise<void>;
}): ShellPorts {
  return {
    notifications: opts.container.getNotificationPort(),
    telemetry: opts.container.getTelemetry(),
    navigation: opts.navigation,
    layout: opts.layout,
    auth: opts.auth,
    appDisplayTitle: opts.appDisplayTitle ?? AppConfig.getAppDisplayTitle(),
    system: opts.container.getSystemServices(),
    entrega: opts.container.getEntregaServices(),
    reposicion: opts.container.getReposicionServices(),
    geolocation: opts.container.getGeolocationPort(),
    entregaMap: opts.container.getEntregaMapPort(),
    entregaPdf: opts.container.getEntregaPdfPort(),
    reposicionPdf: opts.container.getReposicionPdfPort(),
    signatureCapture: opts.container.getSignatureCapturePort(),
    notifyLoginSuccess: opts.notifyLoginSuccess,
    onLocalSqlitePeerUpdate: onLocalSqlitePeerUpdate,
  };
}
